import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { AuthContext } from './AuthContext';

export const PLANS = [
  {
    id: 'free',
    name: 'Starter',
    price: 0,
    label: 'Free',
    desc: 'Get in instantly',
    color: '#6b7280',
    features: [
      'Unlimited inbound calls',
      'Unlimited outbound calls',
      'Browser-based softphone',
      'Global number provisioning',
      'No credit card required',
      'Basic call controls (mute, hold, transfer)',
    ],
  },
  {
    id: 'pro',
    name: 'Pro',
    price: 17,
    label: '$17/mo',
    desc: 'Turn on revenue',
    color: '#3b82f6',
    features: [
      'Everything in Starter',
      'Voicemail inbox',
      'In-app voicemail playback',
      'Two-way SMS messaging',
      'Threaded conversations',
      'Delivery status tracking',
      'Unread notifications',
    ],
  },
  {
    id: 'business',
    name: 'Business',
    price: 27,
    label: '$27/mo',
    desc: 'Full domination',
    color: '#8b5cf6',
    features: [
      'Everything in Pro',
      'Instant video rooms',
      'Scheduled meetings',
      'Guest join links (no login)',
      'Unified communications dashboard',
    ],
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: null,
    label: 'Custom',
    desc: 'Scale without limits',
    color: '#f59e0b',
    features: [
      'Everything in Business',
      'Multi-tenant architecture',
      'Custom integrations',
      'API access',
      'Dedicated support',
    ],
  },
];

export const FEATURE_TIER = {
  softphone : 'free',
  numbers   : 'free',
  voicemail : 'pro',
  sms       : 'pro',
  video     : 'business',
  api       : 'enterprise',
};

const TIER_ORDER = ['free', 'pro', 'business', 'enterprise'];

export const SubscriptionContext = createContext();

export function SubscriptionProvider({ children }) {
  const { isAuthenticated } = useContext(AuthContext);
  const [tier, setTier]       = useState('free');
  const [status, setStatus]   = useState('active');
  const [loading, setLoading] = useState(true);

  const loadSubscription = useCallback(async () => {
    if (!isAuthenticated) { setLoading(false); return; }
    try {
      const { data } = await api.get('/subscription');
      setTier(data.tier);
      setStatus(data.status);
    } catch {
      setTier('free');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => { loadSubscription(); }, [loadSubscription]);

  const canAccess = useCallback((feature) => {
    const required = FEATURE_TIER[feature];
    if (!required) return true;
    return TIER_ORDER.indexOf(tier) >= TIER_ORDER.indexOf(required);
  }, [tier]);

  const upgrade = useCallback(async (newTier) => {
    const { data } = await api.post('/subscription/upgrade', { tier: newTier });
    setTier(data.tier);
    setStatus(data.status);
    return data;
  }, []);

  const planInfo = PLANS.find((p) => p.id === tier) || PLANS[0];

  return (
    <SubscriptionContext.Provider value={{
      tier, status, loading, canAccess, upgrade, loadSubscription, planInfo,
    }}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  return useContext(SubscriptionContext);
}
